import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartColorAt } from '../../../utils/chartColors';
import type { CostChartPoint, CostChartViewMode } from '../costChartTypes';
import { CostTooltip } from './CostTooltip';
import { formatXAxisLabel } from './costChartAggregate';

type CostChartPlotProps = {
  mode: CostChartViewMode;
  chartData: CostChartPoint[];
  methods: string[];
  methodLabels: Record<string, string>;
  locale: string;
  yMax: number | undefined;
  yAxisFormatter: (value: number) => string;
  showRunningTotal: boolean;
  runningTotalLabel: string;
  totalLabel: string;
  inputTokensHint: string;
  outputTokensHint: string;
  showMonthlyLimitLine: boolean;
  showYearlyLimitLine: boolean;
  monthlyCostLimit: number | null;
  yearlyCostLimit: number | null;
  monthlyLimitLabel: string;
  yearlyLimitLabel: string;
};

export function CostChartPlot({
  mode,
  chartData,
  methods,
  methodLabels,
  locale,
  yMax,
  yAxisFormatter,
  showRunningTotal,
  runningTotalLabel,
  totalLabel,
  inputTokensHint,
  outputTokensHint,
  showMonthlyLimitLine,
  showYearlyLimitLine,
  monthlyCostLimit,
  yearlyCostLimit,
  monthlyLimitLabel,
  yearlyLimitLabel,
}: CostChartPlotProps) {
  return (
    <ResponsiveContainer width="100%" height={360}>
      <ComposedChart
        data={chartData}
        margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--color-border)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          tickFormatter={(v) => formatXAxisLabel(v, mode)}
          tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={yAxisFormatter}
          tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
          axisLine={false}
          tickLine={false}
          width={56}
          domain={[0, yMax ?? 'auto']}
        />
        <Tooltip
          wrapperStyle={{ zIndex: 'var(--z-tooltip)' }}
          content={
            <CostTooltip
              locale={locale}
              mode={mode}
              methods={methods}
              methodLabels={methodLabels}
              totalLabel={totalLabel}
              runningTotalLabel={runningTotalLabel}
              inputTokensHint={inputTokensHint}
              outputTokensHint={outputTokensHint}
            />
          }
          cursor={{ fill: 'var(--color-border)', opacity: 0.5 }}
        />
        {methods.length > 0 ? (
          methods.map((method, index) => (
            <Bar
              key={method}
              dataKey={method}
              name={methodLabels[method] ?? method}
              stackId="cost"
              fill={chartColorAt(index)}
              radius={
                index === methods.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]
              }
              maxBarSize={40}
            />
          ))
        ) : (
          <Bar
            dataKey="cost"
            fill="var(--color-accent)"
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          />
        )}
        {showRunningTotal ? (
          <Line
            type="monotone"
            dataKey="runningTotal"
            name={runningTotalLabel}
            stroke="var(--color-text-muted)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            legendType="none"
          />
        ) : (
          <Line dataKey="__hidden" hide legendType="none" />
        )}
        {showMonthlyLimitLine ? (
          <ReferenceLine
            y={monthlyCostLimit ?? undefined}
            stroke="var(--color-danger)"
            strokeWidth={2}
            strokeDasharray="6 4"
            ifOverflow="extendDomain"
            label={{
              value: monthlyLimitLabel,
              position: showYearlyLimitLine
                ? 'insideBottomRight'
                : 'insideTopRight',
              fill: 'var(--color-danger)',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        ) : null}
        {showYearlyLimitLine ? (
          <ReferenceLine
            y={yearlyCostLimit ?? undefined}
            stroke="var(--color-danger)"
            strokeWidth={2}
            strokeDasharray="6 4"
            ifOverflow="extendDomain"
            label={{
              value: yearlyLimitLabel,
              position: 'insideTopRight',
              fill: 'var(--color-danger)',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        ) : null}
        {!showMonthlyLimitLine && !showYearlyLimitLine ? (
          <ReferenceLine y={0} stroke="transparent" />
        ) : null}
        {methods.length > 0 && (
          <Legend
            verticalAlign="bottom"
            height={28}
            iconType="square"
            iconSize={10}
            wrapperStyle={{
              fontSize: '0.75rem',
              color: 'var(--color-text-muted)',
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
